import { z } from 'zod/mini'

import { RequestError } from '../../components/api/extractHttpError'
import type { WidgetState } from '../Copilot'
import type { Feed } from '../useFeed'

export const STORAGE_KEY = 'copilot-state'

export type CopilotPersistentStateV1 = {
  version: 1
  savedAt: number
  session: {
    uid: string
    queue: string
    customer: string
    callState: WidgetState['currentCallState']
    hasTranscription: boolean
    transcription: string
  }
  feed: ({ uid: string } & (
    | { type: 'summary'; data: { summary: string } }
    | {
        type: 'ask-copilot'
        status: 'pending'
        data: {
          chat: SerializedChatResponse
          originalPayload: { question: string; uid: string | null }
        }
      }
    | {
        type: 'ask-copilot'
        status: 'complete' | 'canceled'
        data: { chat: SerializedChatResponse }
      }
    | {
        type: 'ask-copilot'
        status: 'error'
        data: {
          question: string
          error?: {
            name: string
            message: string
            cause?: Record<string, unknown>
          }
          originalPayload: { question: string; uid: string | null }
        }
      }
  ))[]
}

export type HydratedPersistentState = {
  version: 1
  savedAt: number
  session: CopilotPersistentStateV1['session']
  feed: Feed[]
}

type SerializedChatResponse = {
  uid: string
  organizationUid: string
  created: string
  answers: {
    queryId: string
    question: string
    date: string
    reply: string | null
    links: {
      id: number
      title: string
      url: string
      score: number
      language: string
      puzzelKbId: string | null
    }[]
    errorCode: string | null
    errorMessage: string | null
  }[]
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const requestErrorCodec = z.codec(
  z.object({
    name: z.string(),
    message: z.string(),
    cause: z.optional(z.record(z.string(), z.unknown())),
  }),
  z.custom<RequestError>((val): val is RequestError => val instanceof Error),
  {
    decode: (raw) => {
      const err = new RequestError(raw.message, raw.cause || {})
      err.name = raw.name
      return err
    },
    encode: (err) => ({
      name: err.name,
      message: err.message,
      cause: isRecord(err.cause) ? err.cause : undefined,
    }),
  },
)

const linkSchema = z.object({
  id: z.number(),
  title: z.string(),
  url: z.string(),
  score: z.number(),
  language: z.string(),
  puzzelKbId: z.nullable(z.string()),
})

const answerSchema = z.object({
  queryId: z.string(),
  question: z.string(),
  date: z.string(),
  reply: z.nullable(z.string()),
  links: z.array(linkSchema),
  errorCode: z.nullable(z.string()),
  errorMessage: z.nullable(z.string()),
})

const originalPayloadSchema = z.object({
  question: z.string(),
  uid: z.nullable(z.string()),
})

const chatSchema = z.object({
  uid: z.string(),
  organizationUid: z.string(),
  created: z.string(),
  answers: z.array(answerSchema),
})

const summaryItem = z.object({
  uid: z.string(),
  type: z.literal('summary'),
  data: z.object({ summary: z.string() }),
})

const askCopilotPending = z.object({
  uid: z.string(),
  type: z.literal('ask-copilot'),
  status: z.literal('pending'),
  data: z.object({
    chat: chatSchema,
    originalPayload: originalPayloadSchema,
  }),
})

const askCopilotCompleteCanceled = z.object({
  uid: z.string(),
  type: z.literal('ask-copilot'),
  status: z.union([z.literal('complete'), z.literal('canceled')]),
  data: z.object({ chat: chatSchema }),
})

const askCopilotError = z.object({
  uid: z.string(),
  type: z.literal('ask-copilot'),
  status: z.literal('error'),
  data: z.object({
    question: z.string(),
    error: z.optional(requestErrorCodec),
    originalPayload: originalPayloadSchema,
  }),
})

const feedItem = z.union([
  summaryItem,
  askCopilotPending,
  askCopilotCompleteCanceled,
  askCopilotError,
])

const persistentStateV1Schema = z.object({
  version: z.literal(1),
  savedAt: z.number(),
  session: z.object({
    uid: z.string(),
    queue: z.string(),
    customer: z.string(),
    callState: z.enum(['awaiting', 'alerting', 'handling', 'wrapUp']),
    hasTranscription: z.boolean(),
    transcription: z.string(),
  }),
  feed: z.array(feedItem),
})

const convertPendingToError = (
  item: z.infer<typeof feedItem>,
): z.infer<typeof feedItem> => {
  if (item.type === 'ask-copilot' && item.status === 'pending') {
    const latestAnswer = item.data.chat.answers.at(-1)
    const fallbackQuestion = latestAnswer?.question || 'Request interrupted'
    const question = item.data.originalPayload?.question ?? fallbackQuestion
    const originalPayloadUid =
      item.data.originalPayload?.uid === null
        ? null
        : (item.data.originalPayload?.uid ?? item.data.chat.uid)
    const originalPayload = {
      question,
      uid: originalPayloadUid,
    }
    const err = new RequestError('Request interrupted', {
      chatUid: item.data.chat.uid,
      reason: 'interrupted-during-reload',
    })

    return {
      uid: item.uid,
      type: 'ask-copilot',
      status: 'error',
      data: {
        question,
        error: err,
        originalPayload,
      },
    }
  }

  return item
}

export const serializeState = ({
  session,
  feed,
}: {
  session: CopilotPersistentStateV1['session']
  feed: Feed[]
}): CopilotPersistentStateV1 =>
  z.encode(persistentStateV1Schema, {
    version: 1,
    savedAt: Date.now(),
    session,
    feed,
  })

const normalizePendingOriginalPayload = (raw: unknown): unknown => {
  if (!isRecord(raw)) return raw
  if (!Array.isArray(raw.feed)) return raw

  const normalizedFeed = raw.feed.map((item) => {
    if (!isRecord(item)) return item
    if (item.type !== 'ask-copilot' || item.status !== 'pending') return item

    const data = isRecord(item.data) ? item.data : null
    if (!data || data.originalPayload) return item

    const chat = isRecord(data.chat) ? data.chat : null
    const answers = Array.isArray(chat?.answers) ? chat?.answers : []
    const latestAnswer = answers.at(-1)
    const question =
      isRecord(latestAnswer) && typeof latestAnswer.question === 'string'
        ? latestAnswer.question
        : 'Request interrupted'
    const uid = chat && typeof chat.uid === 'string' ? chat.uid : null

    return {
      ...item,
      data: {
        ...data,
        originalPayload: { question, uid },
      },
    }
  })

  return { ...raw, feed: normalizedFeed }
}

export const deserializeState = (
  raw: unknown,
): HydratedPersistentState | null => {
  if (!raw) return null

  // oxlint-disable-next-line typescript/no-explicit-any -- zod safeDecode validates the input at runtime
  let parsed: any
  try {
    parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch {
    return null
  }

  const result = z.safeParse(
    persistentStateV1Schema,
    normalizePendingOriginalPayload(parsed),
  )
  if (!result.success) return null

  const data = result.data
  if (data.version !== 1) return null

  return {
    ...data,
    feed: data.feed.map(convertPendingToError),
  }
}
